import {
  Alert,
  Collapse,
  Grid,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
} from "@mui/material";
import type { Theme } from "@mui/material/styles";
import { createStyles, makeStyles } from "@mui/styles";
import React from "react";
import AddNoteIcon from "src/icons/AddNoteIcon";
import api from "src/store/features/api";
import CountryOrCryptoFlag from "../common/CountryOrCryptoFlag";
import SecondaryButton from "../wallets/Modal/deposit/SecondaryButton";
import { createLinesMobile } from "./helperfns";
import { addTransactionNote, useTransactionSlice } from "src/store/features/transactions.slice";
import { useAppDispatch } from "src/store/store";

const InnerTableStyles = makeStyles((theme: Theme) =>
  createStyles({
    TableRow: {
      "& > *": { borderBottom: "none" },
      cursor: "pointer",
    },
    CollapsedTableHead: {
      "& > *": {
        borderBottom: "none",
        border: "1px solid transparent",
        fontWeight: 600,
        color: "#8d9da7",
        fontSize: "14px",
        padding: 0,
      },
    },
    CollapsedTableBody: {
      "& > *": {
        padding: "16px 0px",
        borderBottom: "none",
        border: "1px solid transparent",
      },
    },
    CollapsedTotalRow: {
      backgroundColor: "#f8f9f9",
      height: "54px",
      "& > *": {
        borderBottom: "none",
        border: "1px solid transparent",
      },
    },
  }),
);

interface InnerTableProps {
  transactionID: string;
  note: string | undefined;
  lines: ReturnType<typeof createLinesMobile>[];
  total: string | number;
}

const InnerTable = (props: InnerTableProps) => {
  const { transactionID, lines, total } = props;
  const { notes } = useTransactionSlice();
  const dispatch = useAppDispatch();
  const classes = InnerTableStyles();
  const [addNewNote, setAddNewNote] = React.useState("");
  const hasNote = addNewNote?.length > 0;
  const [displayNote, setDisplayNote] = React.useState<boolean>(false);
  const [saveNote, setSaveNote] = React.useState<boolean>(false);
  const [notificationOpen, setNotificationOpen] = React.useState(false);
  const [postNote] = api.useAddNoteToCtMutation();

  React.useEffect(() => {
    notes.forEach((note) => {
      if (note.id === transactionID) {
        setAddNewNote(note.note); // set the note in the row
      }
    });
  }, [notes]);

  const handleNote = async (e: any) => {
    e.stopPropagation();
    if (displayNote && addNewNote) {
      const maxNoteLength = 500;
      let newNote;

      if (addNewNote.length > maxNoteLength) {
        newNote = props.note ?? "";
      } else {
        newNote = addNewNote;
      }

      let note = {
        ctNoteDto: {
          ctId: transactionID,
          note: newNote,
        },
      };
      try {
        await postNote(note);
        setNotificationOpen(true);
        setTimeout(() => {
          handleNotificationClose();
        }, 5000);
        setSaveNote(false);
        dispatch(addTransactionNote({ id: transactionID, note: addNewNote }));
      } catch (e) {
        console.warn(e);
      }
    }

    setDisplayNote((prev): boolean => !prev);
  };

  const handleNotificationClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === "clickaway") {
      return;
    }
    setNotificationOpen(false);
  };

  return (
    <>
      <Table
        size="small"
        aria-label="transaction-details"
        sx={{ tableLayout: "fixed", width: "100%" }}>
        <TableHead>
          <TableRow className={classes.CollapsedTableHead}>
            <TableCell colSpan={1} sx={{ width: "10%" }} />
            <TableCell colSpan={1} sx={{ width: "18%" }} />
            <TableCell colSpan={1} align="right" sx={{ width: "16.66%" }}>
              Units
            </TableCell>
            <TableCell colSpan={1} align="right" sx={{ width: "16.66%" }}>
              Unit Price
            </TableCell>
            <TableCell colSpan={1} align="right" sx={{ width: "16.66%" }}>
              Fee
            </TableCell>
            <TableCell colSpan={1} align="right" sx={{ width: "16.66%" }}>
              Value
            </TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {lines.map((line, index) => {
            return (
              <TableRow key={`${line.side}-${index}`} className={classes.CollapsedTableBody}>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    color: "#8d9da7",
                    fontSize: "14px",
                  }}>
                  {line.side}
                </TableCell>
                <TableCell>
                  <Grid container spacing={1} alignItems="center">
                    <Grid item>
                      <CountryOrCryptoFlag
                        name={line.base.logoUrl ? line.base.logoUrl : line.base.code}
                        size={24}
                      />
                    </Grid>
                    <Grid item xs>
                      {line.base.name}
                    </Grid>
                  </Grid>
                </TableCell>
                <TableCell align="right" translate="no">
                  {line.units}
                </TableCell>
                <TableCell align="right" translate="no">
                  {line.price}
                </TableCell>
                <TableCell align="right" translate="no">
                  {line.fee}
                </TableCell>
                <TableCell align="right" translate="no">
                  {line.value}
                </TableCell>
              </TableRow>
            );
          })}
          <TableRow className={classes.CollapsedTotalRow}>
            <TableCell
              colSpan={6}
              align="right"
              sx={{
                fontWeight: 600,
                color: "#000",
                fontSize: "14px",
              }}
              translate="no">
              Total: {total}
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>

      {/* NOTE COMPONENT */}
      <Collapse in={displayNote}>
        <Grid container>
          <Grid item xs={12}>
            <Grid container sx={{ marginBottom: "12px" }}>
              <Grid item xs={12}>
                <TextField
                  placeholder="Enter your note here, e.g. trade for the day"
                  fullWidth={true}
                  multiline
                  defaultValue={addNewNote}
                  onClick={(e) => e.stopPropagation()}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    if (addNewNote !== event.target.value) {
                      setSaveNote(true);
                    }
                    setAddNewNote(event.target.value);
                  }}
                  rows={2}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Collapse>

      {hasNote && !saveNote ? (
        <Grid item xs>
          <SecondaryButton
            onClick={handleNote}
            endIcon={<AddNoteIcon height="20" width="20" color="#fff" />}>
            See note
          </SecondaryButton>
        </Grid>
      ) : (
        <Grid item xs>
          <SecondaryButton
            onClick={handleNote}
            endIcon={<AddNoteIcon height="20" width="20" color="#fff" />}>
            {displayNote ? "Save Note" : "Add Note"}
          </SecondaryButton>
        </Grid>
      )}
      <Snackbar
        open={notificationOpen}
        autoHideDuration={6000}
        onClose={handleNotificationClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        <Alert onClose={handleNotificationClose} severity="success" sx={{ width: "100%" }}>
          Note successfully saved!
        </Alert>
      </Snackbar>
    </>
  );
};

export default InnerTable;
